<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BulkIndexContractServiceFee',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    items: {
      type: Array,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('right');
  const formId = ref(null);
  const formName = ref('BulkIndexContractServiceFee');
  const item = ref({
    percentage: null,
    new_indexation_date: null,
    valid_from: null,
    transaction_notes: null,
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    formId: formId.value,
    formName: formName.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'formId' },
      { name: 'formName' },
      {
        name: 'item',
        cache: { type: 'local', key: 'BulkIndexContractServiceFee.item' },
      },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId },
    ux: {},
    dataRefs: {
      labelAlign,
      formId,
      formName,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'BulkIndexContractServiceFee|Title',
        'Indexing'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.items,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('items');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_8dmr3rwzg_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_nd55doegm_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_request_mn4sow89x_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_index_contract_service_fee',
      data: {
        items: _.map(props.items, (p) =>
          _.extend(_.pick(p, ['id', 'etag']), { value: item.value })
        ),
      },
    };
  };

  const act_7315092382398562_closeModal_8dmr3rwzg =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_8dmr3rwzg_getActionArgs,
    });
  const inst_evh_7315092382398562_close_2l6nmkmgk =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_8dmr3rwzg,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_2l6nmkmgk],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_ye0aevgu2 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_5kec508w0 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ye0aevgu2,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_5kec508w0],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_irsvswczt =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_wicwdx8xe =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_irsvswczt,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_wicwdx8xe],
    event: 'close',
  });
  const act_1040167445267876_closeModal_nd55doegm =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_nd55doegm_getActionArgs,
    });
  const inst_evh_1040167445267876_success_b0z7iqze5 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_nd55doegm,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_mn4sow89x = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_mn4sow89x_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_b0z7iqze5],
  });
  const inst_evh_1040167445267876_submit_twtsro9re =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_mn4sow89x,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_twtsro9re],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_oy7s4rav0 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_zkbxb99kw =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_oy7s4rav0,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_zkbxb99kw],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_2l6nmkmgk,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_5kec508w0,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_wicwdx8xe,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_b0z7iqze5,
    inst_evh_1040167445267876_submit_twtsro9re,
    submit: inst_evh_1040167445267876_submit_twtsro9re,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_zkbxb99kw,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_2l6nmkmgk = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_2l6nmkmgk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_5kec508w0 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_5kec508w0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_wicwdx8xe = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_wicwdx8xe.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_b0z7iqze5 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_b0z7iqze5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_twtsro9re = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_twtsro9re.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_zkbxb99kw = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_zkbxb99kw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="BulkIndexContractServiceFee"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :resourceTitle="
          pgettext(
            'BulkIndexContractServiceFee|ResourceTitle',
            'Contract Service Fees'
          )
        "
        :resourceKey="
          pgettext('BulkIndexContractServiceFee|ResourceKey', 'Indexing')
        "
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        autofocus="first"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="percentage"
          :label="pgettext('BulkIndexContractServiceFee', 'Percentage')"
          :description="
            pgettext(
              'BulkIndexContractServiceFee',
              'The percentage used to calculate the indexed prices.'
            )
          "
          :label-cols-md="3"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
          appendText="%"
          required
        >
          <ItpFormInput
            name="percentage"
            v-model="item.percentage"
            mask="number"
            unmask="typed"
            :scale="4"
            required
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="valid_from"
          :label="pgettext('BulkIndexContractServiceFee', 'Valid From')"
          :description="
            pgettext(
              'BulkIndexContractServiceFee',
              'The start date from which the new fee is valid.'
            )
          "
          :label-cols-md="3"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormDatetime name="valid_from" v-model="item.valid_from" required>
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="new_indexation_date"
          :label="
            pgettext('BulkIndexContractServiceFee', 'New Indexation Date')
          "
          :description="
            pgettext(
              'BulkIndexContractServiceFee',
              'Sets the next indexation date for the related contracts.'
            )
          "
          :label-cols-md="3"
          :content-cols-md="4"
          :labelAlign="labelAlign"
        >
          <ItpFormDatetime
            name="new_indexation_date"
            v-model="item.new_indexation_date"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="transaction_notes"
          :label="pgettext('ContractServiceFee', 'Transaction notes')"
          :label-cols-md="3"
          :content-cols-md="9"
          :labelAlign="labelAlign"
        >
          <TransactionNotesEditor v-model="item.transaction_notes">
          </TransactionNotesEditor>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Submit')"
          :disabled="!forms[formName] || !forms[formName].submittable"
          type="submit"
          :form="formId"
          variant="primary"
          :icon="$config.submitIcon"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
